import React, { useState, useEffect } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import ImageUpload from "./ImageUpload";
import ImageManagement from "./ImageManagement";
import { uploadCharacterImage, getCharacterImages } from "../utils/characterUtils";
import { getUserId } from "../utils/userUtils";

const CharacterEditForm = ({ character, onUpdateCharacter, onCancel, isLoading = false }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    personality_prompt: "",
    system_prompt: "",
    is_public: false,
    status_emoji: "🔥",
    short_description: "",
    creator_name: ""
  });

  const [errors, setErrors] = useState({});
  
  // Image management state
  const [currentImages, setCurrentImages] = useState({
    avatar_url: "",
    selfie_images: [],
    total_selfie_size: 0
  });
  const [newAvatarImage, setNewAvatarImage] = useState(null);
  const [newSelfieImages, setNewSelfieImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [showAdvancedImageManagement, setShowAdvancedImageManagement] = useState(false);

  // Pre-populate form with character data when component mounts or character changes
  useEffect(() => {
    if (character) {
      setFormData({
        name: character.name || "",
        description: character.description || "",
        personality_prompt: character.personality_prompt || "",
        system_prompt: character.system_prompt || "",
        is_public: character.is_public || false,
        status_emoji: character.status_emoji || "🔥",
        short_description: character.short_description || "",
        creator_name: character.creator_name || ""
      });
      
      // Load existing images
      setCurrentImages({
        avatar_url: character.avatar_url || "",
        selfie_images: character.selfie_images || [],
        total_selfie_size: character.total_selfie_size || 0
      });
      
      // Clear any existing errors and new image uploads when loading new character data
      setErrors({});
      setNewAvatarImage(null);
      setNewSelfieImages([]);
      setUploadingImages(false);
    }
  }, [character]);
  
  // Available status emojis for selection
  const statusEmojis = ["🔥", "🧪", "⭐", "💫", "🚀", "🎭", "🤖", "💎"];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Character name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Character name must be at least 2 characters";
    }
    
    if (!formData.description.trim()) {
      newErrors.description = "Character description is required";
    } else if (formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }
    
    if (!formData.personality_prompt.trim()) {
      newErrors.personality_prompt = "Personality description is required";
    } else if (formData.personality_prompt.trim().length < 20) {
      newErrors.personality_prompt = "Personality description must be at least 20 characters";
    }
    
    // Validate publication fields if character is being published
    if (formData.is_public) {
      if (!formData.short_description.trim()) {
        newErrors.short_description = "Short description is required for published characters";
      } else if (formData.short_description.trim().length < 10) {
        newErrors.short_description = "Short description must be at least 10 characters";
      } else if (formData.short_description.trim().length > 150) {
        newErrors.short_description = "Short description must be less than 150 characters";
      }
      
      if (!formData.creator_name.trim()) {
        newErrors.creator_name = "Creator name is required for published characters";
      } else if (formData.creator_name.trim().length < 2) {
        newErrors.creator_name = "Creator name must be at least 2 characters";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUploads = async (characterId, userId) => {
    setUploadingImages(true);
    
    try {
      // Upload new avatar if present
      if (newAvatarImage) {
        console.log('Uploading avatar:', newAvatarImage);
        if (!newAvatarImage.base64 || !newAvatarImage.name) {
          throw new Error('Avatar image data is incomplete');
        }
        await uploadCharacterImage(characterId, userId, newAvatarImage, 'avatar');
      }
      
      // Upload new selfies if present
      for (const selfieImage of newSelfieImages) {
        console.log('Uploading selfie:', selfieImage);
        if (!selfieImage.base64 || !selfieImage.name) {
          throw new Error('Selfie image data is incomplete');
        }
        await uploadCharacterImage(characterId, userId, selfieImage, 'selfie');
      }
      
      console.log('All images uploaded successfully');
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error; // Re-throw to handle in submit
    } finally {
      setUploadingImages(false);
    }
  };

  // Handler for advanced image management component
  const handleImageUpload = async (imageData, type) => {
    try {
      const userId = getUserId(user);
      if (!userId) {
        throw new Error("User ID not available");
      }
      
      setUploadingImages(true);
      await uploadCharacterImage(character.character_id, userId, imageData, type);
      
      // Refresh current images
      const updatedImages = await getCharacterImages(character.character_id, userId);
      setCurrentImages(updatedImages);
      
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(`Error uploading ${type}: ${error.message}`);
    } finally {
      setUploadingImages(false);
    }
  };

  // Handler for image deletion (placeholder - would need backend implementation)
  const handleImageDelete = async (image, type, index) => {
    try {
      // Note: This would require a backend endpoint for image deletion
      console.log(`Delete ${type} image:`, image, 'at index:', index);
      alert('Image deletion functionality would be implemented here');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert(`Error deleting ${type}: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Prepare character data for update - trim all values
      const characterData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        personality_prompt: formData.personality_prompt.trim(),
        system_prompt: formData.system_prompt.trim() || "",
        is_public: formData.is_public,
        status_emoji: formData.status_emoji,
        short_description: formData.short_description.trim(),
        creator_name: formData.creator_name.trim()
      };
      
      try {
        // Update character data first
        await onUpdateCharacter(character.character_id, characterData);
        
        // Upload new images if any
        if (newAvatarImage || newSelfieImages.length > 0) {
          // Get user_id from Amplify auth
          const userId = getUserId(user);
          
          console.log('User ID debugging:', {
            amplifyUser: user,
            extractedUserId: userId,
            character
          });
          
          if (!userId) {
            throw new Error("User ID not available - please ensure you are logged in");
          }
          
          await handleImageUploads(character.character_id, userId);
          
          // Clear new image uploads after successful upload
          setNewAvatarImage(null);
          setNewSelfieImages([]);
        }
      } catch (error) {
        console.error('Error updating character:', error);
        // Error handling - the parent component should handle this
      }
    }
  };

  const handleCancel = () => {
    // Reset form to original character data
    if (character) {
      setFormData({
        name: character.name || "",
        description: character.description || "",
        personality_prompt: character.personality_prompt || "",
        system_prompt: character.system_prompt || "",
        is_public: character.is_public || false,
        status_emoji: character.status_emoji || "🔥",
        short_description: character.short_description || "",
        creator_name: character.creator_name || ""
      });
    }
    setErrors({});
    onCancel();
  };

  // Don't render if no character data
  if (!character) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-semibold text-white mb-2">Edit Character</h2>
        <p className="text-gray-400 text-sm mb-6">
          Editing: <span className="text-white font-medium">{character.name}</span>
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Character Name */}
          <div>
            <label htmlFor="edit-name" className="block text-sm font-medium text-gray-300 mb-2">
              Character Name *
            </label>
            <input
              type="text"
              id="edit-name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? "border-red-500" : "border-gray-600"
              }`}
              placeholder="Enter character name"
              disabled={isLoading}
            />
            {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
          </div>

          {/* Character Description */}
          <div>
            <label htmlFor="edit-description" className="block text-sm font-medium text-gray-300 mb-2">
              Character Description *
            </label>
            <textarea
              id="edit-description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                errors.description ? "border-red-500" : "border-gray-600"
              }`}
              placeholder="Describe your character's role and purpose"
              disabled={isLoading}
            />
            {errors.description && <p className="text-red-400 text-sm mt-1">{errors.description}</p>}
          </div>

          {/* Personality Prompt */}
          <div>
            <label htmlFor="edit-personality" className="block text-sm font-medium text-gray-300 mb-2">
              Personality & Behavior *
            </label>
            <textarea
              id="edit-personality"
              name="personality_prompt"
              value={formData.personality_prompt}
              onChange={handleInputChange}
              rows={4}
              className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                errors.personality_prompt ? "border-red-500" : "border-gray-600"
              }`}
              placeholder="Describe how your character should behave, their personality traits, communication style, etc."
              disabled={isLoading}
            />
            {errors.personality_prompt && <p className="text-red-400 text-sm mt-1">{errors.personality_prompt}</p>}
          </div>

          {/* System Prompt (Optional) */}
          <div>
            <label htmlFor="edit-system" className="block text-sm font-medium text-gray-300 mb-2">
              Advanced Instructions (Optional)
            </label>
            <textarea
              id="edit-system"
              name="system_prompt"
              value={formData.system_prompt}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical"
              placeholder="Additional system instructions or constraints (optional)"
              disabled={isLoading}
            />
          </div>

          {/* Character Visibility Section */}
          <div className="border-t border-gray-600 pt-6">
            <h3 className="text-lg font-medium text-white mb-4">Character Visibility</h3>
            
            {/* Publish to Character Board Toggle */}
            <div className="mb-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="is_public"
                  checked={formData.is_public}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  className="sr-only"
                />
                <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${
                  formData.is_public ? 'bg-blue-600' : 'bg-gray-600'
                }`}>
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ease-in-out ${
                    formData.is_public ? 'transform translate-x-5' : ''
                  }`}></div>
                </div>
                <span className="ml-3 text-sm text-gray-300">
                  Publish to Character Board
                </span>
              </label>
              <p className="text-gray-400 text-xs mt-1 ml-14">
                Make this character discoverable by all users on the public character board
              </p>
            </div>

            {/* Publication Fields - Only show when character is set to public */}
            {formData.is_public && (
              <div className="space-y-4 ml-4 border-l-2 border-blue-600 pl-4">
                {/* Status Emoji Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Status Emoji
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {statusEmojis.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, status_emoji: emoji }))}
                        className={`w-10 h-10 text-xl rounded-lg border-2 transition-all duration-200 flex items-center justify-center ${
                          formData.status_emoji === emoji
                            ? 'border-blue-500 bg-blue-900 bg-opacity-30'
                            : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                        }`}
                        disabled={isLoading}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                  <p className="text-gray-400 text-xs mt-1">
                    Choose an emoji to represent your character's status or theme
                  </p>
                </div>

                {/* Short Description */}
                <div>
                  <label htmlFor="edit-short-description" className="block text-sm font-medium text-gray-300 mb-2">
                    Short Description for Character Board *
                  </label>
                  <textarea
                    id="edit-short-description"
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleInputChange}
                    rows={2}
                    maxLength={150}
                    className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                      errors.short_description ? "border-red-500" : "border-gray-600"
                    }`}
                    placeholder="Brief description for the character board (max 150 characters)"
                    disabled={isLoading}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.short_description && <p className="text-red-400 text-xs">{errors.short_description}</p>}
                    <p className="text-gray-400 text-xs ml-auto">
                      {formData.short_description.length}/150
                    </p>
                  </div>
                </div>

                {/* Creator Name */}
                <div>
                  <label htmlFor="edit-creator-name" className="block text-sm font-medium text-gray-300 mb-2">
                    Creator Name *
                  </label>
                  <input
                    type="text"
                    id="edit-creator-name"
                    name="creator_name"
                    value={formData.creator_name}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.creator_name ? "border-red-500" : "border-gray-600"
                    }`}
                    placeholder="Your name or display name as the creator"
                    disabled={isLoading}
                  />
                  {errors.creator_name && <p className="text-red-400 text-xs mt-1">{errors.creator_name}</p>}
                </div>

                {/* Publication Date Display */}
                {character.published_at && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Published Date
                    </label>
                    <div className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-400 text-sm">
                      {new Date(character.published_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                )}

                {/* Character Board Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Character Board Preview
                  </label>
                  <div className="bg-gray-700 border border-gray-600 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-12 h-12 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden">
                        {currentImages.avatar_url ? (
                          <img 
                            src={currentImages.avatar_url} 
                            alt="Character preview" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-gray-400 text-xs">No Image</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="text-white font-medium truncate">
                            {formData.name || 'Character Name'}
                          </h4>
                          <span className="text-lg">{formData.status_emoji}</span>
                          <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded">
                            Community
                          </span>
                        </div>
                        <p className="text-gray-300 text-sm mb-2 line-clamp-2">
                          {formData.short_description || 'Short description will appear here...'}
                        </p>
                        <p className="text-gray-400 text-xs">
                          by {formData.creator_name || 'Creator Name'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-400 text-xs mt-1">
                    This is how your character will appear on the Character Discovery Board
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Character Images Section */}
          <div className="border-t border-gray-600 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-white">Character Images</h3>
              <button
                type="button"
                onClick={() => setShowAdvancedImageManagement(!showAdvancedImageManagement)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
              >
                {showAdvancedImageManagement ? 'Simple View' : 'Advanced Management'}
              </button>
            </div>
            
            {showAdvancedImageManagement ? (
              <ImageManagement
                currentAvatar={currentImages.avatar_url}
                currentSelfies={currentImages.selfie_images}
                onImageUpload={handleImageUpload}
                onImageDelete={handleImageDelete}
                totalSelfieSize={currentImages.total_selfie_size}
              />
            ) : (
              <div>
              {/* Current Avatar */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Current Avatar
              </label>
              {currentImages.avatar_url ? (
                <div className="flex items-center space-x-4">
                  <img 
                    src={currentImages.avatar_url} 
                    alt="Character avatar" 
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-600"
                  />
                  <span className="text-gray-400 text-sm">Avatar uploaded</span>
                </div>
              ) : (
                <div className="text-gray-400 text-sm">No avatar uploaded</div>
              )}
            </div>

            {/* Current Selfies */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Current Selfies ({currentImages.selfie_images.length} images)
              </label>
              {currentImages.selfie_images.length > 0 ? (
                <div className="grid grid-cols-4 gap-2">
                  {currentImages.selfie_images.map((selfie, index) => (
                    <img
                      key={index}
                      src={selfie}
                      alt={`Character selfie ${index + 1}`}
                      className="w-16 h-16 rounded-lg object-cover border border-gray-600"
                    />
                  ))}
                </div>
              ) : (
                <div className="text-gray-400 text-sm">No selfies uploaded</div>
              )}
              {currentImages.total_selfie_size > 0 && (
                <div className="text-gray-400 text-xs mt-1">
                  Total size: {(currentImages.total_selfie_size / (1024 * 1024)).toFixed(1)}MB / 20MB
                </div>
              )}
            </div>

            {/* Upload New Avatar */}
            <div className="mb-6">
              <ImageUpload
                type="avatar"
                onImageSelect={setNewAvatarImage}
                maxSize={1}
                disabled={uploadingImages || isLoading}
              />
            </div>

            {/* Upload New Selfies */}
            <div className="mb-6">
              <ImageUpload
                type="selfie"
                onImageSelect={(imageData) => {
                  if (imageData) {
                    setNewSelfieImages(prev => [...prev, imageData]);
                  }
                }}
                maxSize={5}
                disabled={uploadingImages || isLoading}
              />
              {newSelfieImages.length > 0 && (
                <div className="mt-2">
                  <p className="text-gray-400 text-sm mb-2">New selfies to upload:</p>
                  <div className="grid grid-cols-4 gap-2">
                    {newSelfieImages.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image.preview}
                          alt={`New selfie ${index + 1}`}
                          className="w-16 h-16 rounded-lg object-cover border border-gray-600"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setNewSelfieImages(prev => prev.filter((_, i) => i !== index));
                          }}
                          className="absolute -top-1 -right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Image Upload Status */}
            {uploadingImages && (
              <div className="bg-blue-900 bg-opacity-30 border border-blue-600 rounded-lg p-3 mb-4">
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                  <span className="text-blue-200 text-sm">Uploading images...</span>
                </div>
              </div>
            )}
            </div>
            )}
          </div>

          {/* Security Notice */}
          <div className="bg-blue-900 bg-opacity-30 border border-blue-600 rounded-lg p-3">
            <p className="text-blue-200 text-sm">
              <span className="font-medium">Security:</span> You can only edit characters you created. 
              Changes will be applied immediately to new conversations.
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              disabled={isLoading || uploadingImages}
            >
              {isLoading || uploadingImages ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {uploadingImages ? "Uploading Images..." : "Updating..."}
                </>
              ) : (
                "Update Character"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CharacterEditForm;